import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText } from 'lucide-react';
interface StoryboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function StoryboardModal({
  isOpen,
  onClose
}: StoryboardModalProps) {
  return <AnimatePresence data-id="element-110">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-50 flex flex-col bg-slate-900/60 backdrop-blur-md" data-id="element-111">
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="mt-auto h-[90%] bg-white rounded-t-3xl shadow-2xl flex flex-col overflow-hidden" data-id="element-112">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white z-10 shrink-0" data-id="element-113">
              <div className="flex items-center gap-2" data-id="element-114">
                <div className="w-8 h-8 rounded-full bg-minerva-blue/10 flex items-center justify-center" data-id="element-115">
                  <FileText size={16} className="text-minerva-blue" data-id="element-116" />
                </div>
                <div data-id="element-117">
                  <h3 className="text-sm font-bold text-slate-900" data-id="element-118">
                    Strategic Brief
                  </h3>
                  <p className="text-[11px] font-medium text-slate-500" data-id="element-119">
                    Minerva SNMMI Congress Snak™
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors" data-id="element-120">
                <X size={18} data-id="element-121" />
              </button>
            </div>

            {/* Content (Scrollable) */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 text-slate-800" data-id="element-122">
              <div className="max-w-2xl mx-auto space-y-8 pb-10" data-id="element-123">
                <section data-id="element-124">
                  <h2 className="text-xl font-display font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2" data-id="element-125">
                    Situation
                  </h2>
                  <p className="text-sm leading-relaxed mb-3" data-id="element-126">
                    Minerva Imaging will be present at the SNMMI Annual Meeting
                    2026, one of the world's most important congresses for
                    nuclear medicine, molecular imaging, theranostics, and
                    radiopharmaceutical development.
                  </p>
                  <p className="text-sm leading-relaxed mb-3" data-id="element-127">
                    SNMMI attracts a highly relevant audience across the
                    radiopharmaceutical ecosystem, including: translational
                    scientists, radiochemists, preclinical development teams,
                    CMC / manufacturing leaders, clinical development teams,
                    biotech executives, business development professionals, and
                    strategic partnership stakeholders.
                  </p>
                  <p className="text-sm leading-relaxed mb-3" data-id="element-128">
                    Minerva operates in a highly specialized and increasingly
                    competitive category, where awareness alone is insufficient.
                    Attendees are exposed to competing CROs, CDMOs, imaging
                    companies, manufacturing providers, isotope suppliers, and
                    integrated development platforms.
                  </p>
                  <p className="text-sm leading-relaxed font-semibold text-minerva-blue" data-id="element-129">
                    The challenge is therefore not simply visibility. The
                    challenge is: stopping relevant attendees, creating
                    meaningful engagement, generating qualified leads, and
                    simultaneously learning how the market perceives key
                    industry bottlenecks and Minerva's relevance within them.
                  </p>
                </section>

                <section data-id="element-130">
                  <h2 className="text-xl font-display font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2" data-id="element-131">
                    Purpose
                  </h2>
                  <p className="text-sm leading-relaxed mb-3" data-id="element-132">
                    Create a short, premium, interactive congress experience
                    that transforms passive congress traffic into: active
                    engagement, qualified lead capture, market intelligence,
                    stronger brand awareness, and booth conversations.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-133">
                    <div className="bg-white p-3 rounded-xl border border-slate-200" data-id="element-134">
                      <strong className="text-slate-900 block mb-1" data-id="element-135">
                        The Snak should feel:
                      </strong>
                      <ul className="list-disc pl-4 space-y-1 text-slate-600" data-id="element-136">
                        <li data-id="element-137">Intelligent</li>
                        <li data-id="element-138">Relevant</li>
                        <li data-id="element-139">Visually premium</li>
                        <li data-id="element-140">Fast & rewarding</li>
                        <li data-id="element-141">Congress-native</li>
                      </ul>
                    </div>
                    <div className="bg-white p-3 rounded-xl border border-slate-200" data-id="element-142">
                      <strong className="text-slate-900 block mb-1" data-id="element-143">
                        Not like:
                      </strong>
                      <ul className="list-disc pl-4 space-y-1 text-slate-600" data-id="element-144">
                        <li data-id="element-145">A survey</li>
                        <li data-id="element-146">A lead form</li>
                        <li data-id="element-147">A promotional brochure</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section data-id="element-148">
                  <h2 className="text-xl font-display font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2" data-id="element-149">
                    Strategic Concept
                  </h2>
                  <h3 className="text-lg font-bold text-minerva-blue mb-2" data-id="element-150">
                    "The SNMMI Industry Pulse"
                  </h3>
                  <p className="text-sm leading-relaxed mb-3" data-id="element-151">
                    A short interactive benchmarking experience where attendees
                    compare their own perspective on key radiopharma development
                    challenges against their peers.
                  </p>
                  <p className="text-sm leading-relaxed mb-4" data-id="element-152">
                    The experience combines curiosity-driven questions, peer
                    comparison rewards, authoritative industry insight, indirect
                    brand perception mapping, and a prize-led conversion
                    incentive.
                  </p>

                  {/* Aisle Breaker framing */}
                  <div className="bg-gradient-to-br from-minerva-blue/5 to-cyan-50 p-4 rounded-xl border border-minerva-blue/20 mb-3" data-id="element-153">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-minerva-blue text-white text-[10px] font-bold tracking-[0.2em] uppercase mb-3" data-id="element-154">
                      Aisle Breaker
                    </div>
                    <p className="text-sm leading-relaxed text-slate-700 mb-3" data-id="element-155">
                      The Snak is, first and foremost, an{' '}
                      <span className="font-semibold text-slate-900" data-id="element-156">
                        aisle breaker
                      </span>{' '}
                      — an excuse to engage delegates passing by, with a hook
                      and a question that opens the door to a deeper
                      conversation.
                    </p>
                    <p className="text-sm leading-relaxed text-slate-700 mb-3" data-id="element-157">
                      But it does more than start a chat. It{' '}
                      <span className="font-semibold text-slate-900" data-id="element-158">
                        lifts Minerva above the average
                      </span>{' '}
                      — taking an active role in talking{' '}
                      <span className="italic" data-id="element-159">business strategy</span>, not
                      just "bolts and nuts."
                    </p>
                    <p className="text-sm leading-relaxed text-slate-700" data-id="element-160">
                      And by delivering an engaging, visually premium, fun
                      interactive moment, the Snak further cements that{' '}
                      <span className="font-semibold text-slate-900" data-id="element-161">
                        Minerva is on the forefront of technology and approach
                      </span>{' '}
                      — a partner that thinks differently and shows up
                      differently.
                    </p>
                  </div>
                </section>

                <section data-id="element-162">
                  <h2 className="text-xl font-display font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2" data-id="element-163">
                    Core Objectives
                  </h2>
                  <div className="space-y-5 text-sm" data-id="element-164">
                    {/* Objective 1 */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-165">
                      <div className="flex items-start gap-3 mb-3" data-id="element-166">
                        <div className="shrink-0 w-7 h-7 rounded-full bg-minerva-blue text-white text-xs font-bold flex items-center justify-center" data-id="element-167">
                          1
                        </div>
                        <div data-id="element-168">
                          <h3 className="text-base font-bold text-slate-900 leading-tight" data-id="element-169">
                            Lead Generation
                          </h3>
                          <p className="text-slate-600 text-[13px] mt-1" data-id="element-170">
                            Turn engagement into identifiable commercial
                            opportunities.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3 pl-10" data-id="element-171">
                        <div data-id="element-172">
                          <p className="text-[11px] font-bold tracking-widest uppercase text-minerva-blue mb-1" data-id="element-173">
                            a) Contact Capture
                          </p>
                          <p className="text-slate-700 mb-1" data-id="element-174">
                            Number of participants providing:
                          </p>
                          <ul className="list-disc pl-5 text-slate-600 space-y-0.5" data-id="element-175">
                            <li data-id="element-176">name</li>
                            <li data-id="element-177">email</li>
                            <li data-id="element-178">company</li>
                            <li data-id="element-179">role</li>
                          </ul>
                          <p className="text-[12px] font-semibold text-slate-800 mt-2 italic" data-id="element-180">
                            This is the hard lead KPI.
                          </p>
                        </div>

                        <div data-id="element-181">
                          <p className="text-[11px] font-bold tracking-widest uppercase text-minerva-blue mb-1" data-id="element-182">
                            b) Booth Conversion
                          </p>
                          <p className="text-slate-700 mb-1" data-id="element-183">
                            Number of participants who physically engage further
                            with Minerva at the booth. Measured by:
                          </p>
                          <ul className="list-disc pl-5 text-slate-600 space-y-0.5" data-id="element-184">
                            <li data-id="element-185">"double your chances" activations</li>
                            <li data-id="element-186">rep conversations</li>
                            <li data-id="element-187">booked follow-up meetings</li>
                            <li data-id="element-188">qualitative booth engagement uplift</li>
                          </ul>
                          <p className="text-[12px] text-slate-700 mt-2 italic" data-id="element-189">
                            Because an email alone is not enough. The real value
                            is:{' '}
                            <span className="font-semibold text-slate-900 not-italic" data-id="element-190">
                              conversation + qualification
                            </span>
                            .
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Objective 2 */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-191">
                      <div className="flex items-start gap-3 mb-3" data-id="element-192">
                        <div className="shrink-0 w-7 h-7 rounded-full bg-minerva-blue text-white text-xs font-bold flex items-center justify-center" data-id="element-193">
                          2
                        </div>
                        <div data-id="element-194">
                          <h3 className="text-base font-bold text-slate-900 leading-tight" data-id="element-195">
                            Industry Insights / Market Intelligence
                          </h3>
                          <p className="text-slate-600 text-[13px] mt-1" data-id="element-196">
                            Use the experience to gather commercially useful
                            market intelligence.
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3 pl-10" data-id="element-197">
                        <div data-id="element-198">
                          <p className="text-[11px] font-bold tracking-widest uppercase text-minerva-blue mb-1" data-id="element-199">
                            a) Buying Criteria by Segment
                          </p>
                          <p className="text-slate-700 mb-1" data-id="element-200">
                            Understand what matters most when selecting a
                            CRO/CDMO across different audience groups. Examples:
                          </p>
                          <ul className="list-disc pl-5 text-slate-600 space-y-0.5" data-id="element-201">
                            <li data-id="element-202">scientists → scientific credibility?</li>
                            <li data-id="element-203">program leaders → speed?</li>
                            <li data-id="element-204">CMC → GMP confidence?</li>
                            <li data-id="element-205">BD → integration?</li>
                          </ul>
                          <p className="text-[12px] font-semibold text-slate-800 mt-2 italic" data-id="element-206">
                            This creates real segmentation intelligence.
                          </p>
                        </div>

                        <div data-id="element-207">
                          <p className="text-[11px] font-bold tracking-widest uppercase text-minerva-blue mb-1" data-id="element-208">
                            b) Market Perception of Minerva
                          </p>
                          <p className="text-slate-700 mb-1" data-id="element-209">
                            Understand how the market associates Minerva with:
                          </p>
                          <ul className="list-disc pl-5 text-slate-600 space-y-0.5" data-id="element-210">
                            <li data-id="element-211">solving key radiopharma bottlenecks</li>
                            <li data-id="element-212">partner selection strengths</li>
                            <li data-id="element-213">future capability areas</li>
                          </ul>
                          <p className="text-[12px] text-slate-700 mt-2 italic" data-id="element-214">
                            This becomes brand perception intelligence. Not
                            vanity awareness.{' '}
                            <span className="font-semibold text-slate-900 not-italic" data-id="element-215">
                              Real positioning data.
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Success Definition (kept) */}
                    <div className="bg-minerva-blue/5 p-4 rounded-xl border border-minerva-blue/20 mt-4" data-id="element-216">
                      <strong className="text-minerva-blue block mb-2" data-id="element-217">
                        Success Definition
                      </strong>
                      <p className="text-slate-700 italic" data-id="element-218">
                        Success is NOT: badge scans.
                        <br data-id="element-219" />
                        Success is: engaged, qualified industry conversations
                        backed by measurable market insight and stronger Minerva
                        positioning.
                      </p>
                    </div>
                  </div>
                </section>

                <section data-id="element-220">
                  <h2 className="text-xl font-display font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2" data-id="element-221">
                    Core Experience Flow
                  </h2>
                  <ol className="space-y-4 text-sm" data-id="element-222">
                    <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-223">
                      <strong className="text-slate-900 block text-base mb-2" data-id="element-224">
                        1. Hook / Promise
                      </strong>
                      <p className="text-slate-600 mb-3" data-id="element-225">
                        The opening moment must stop the attendee in three
                        layers — visual credibility, intellectual curiosity, and
                        emotional reward.
                      </p>
                      <div className="space-y-2" data-id="element-226">
                        <div className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-227">
                          <span className="shrink-0 mt-0.5 text-[10px] font-bold tracking-widest uppercase text-minerva-blue bg-minerva-blue/10 px-2 py-0.5 rounded-full" data-id="element-228">
                            Logos
                          </span>
                          <p className="text-slate-700 text-[13px] leading-snug" data-id="element-229">
                            Minerva + SNMMI lockup establishes instant
                            credibility and congress relevance.
                          </p>
                        </div>
                        <div className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-id="element-230">
                          <span className="shrink-0 mt-0.5 text-[10px] font-bold tracking-widest uppercase text-minerva-blue bg-minerva-blue/10 px-2 py-0.5 rounded-full" data-id="element-231">
                            Logos
                          </span>
                          <p className="text-slate-700 text-[13px] leading-snug" data-id="element-232">
                            <span className="font-semibold text-slate-900" data-id="element-233">
                              "See what your peers think"
                            </span>{' '}
                            — the curiosity-driven promise that benchmarks the
                            attendee against the industry in ~60 seconds.
                          </p>
                        </div>
                        <div className="flex items-start gap-3 p-2.5 rounded-lg bg-amber-50 border border-amber-100" data-id="element-234">
                          <span className="shrink-0 mt-0.5 text-[10px] font-bold tracking-widest uppercase text-amber-700 bg-amber-200/50 px-2 py-0.5 rounded-full" data-id="element-235">
                            Pathos
                          </span>
                          <p className="text-slate-700 text-[13px] leading-snug" data-id="element-236">
                            <span className="font-semibold text-slate-900" data-id="element-237">
                              Win cool stuff.
                            </span>{' '}
                            Premium daily raffle (AirPods Pro, Apple Watch,
                            Bose, Sonos, Kindle) with double odds at booth
                            #1762.
                          </p>
                        </div>
                      </div>
                    </li>
                    <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-238">
                      <strong className="text-slate-900 block text-base mb-1" data-id="element-239">
                        2. Industry Challenge Questions
                      </strong>
                      <p className="text-slate-600" data-id="element-240">
                        Three short strategic questions around radiopharma
                        bottlenecks, partner selection factors, and future
                        critical capabilities.
                      </p>
                    </li>
                    <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-241">
                      <strong className="text-slate-900 block text-base mb-1" data-id="element-242">
                        3. Reward / Insight Screens
                      </strong>
                      <p className="text-slate-600" data-id="element-243">
                        After each answer: attendee sees peer poll comparison
                        ("X% answered like you"), premium animated visual, and
                        authoritative industry-style insight quote.
                      </p>
                    </li>
                    <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-244">
                      <strong className="text-slate-900 block text-base mb-1" data-id="element-245">
                        4. Bayesian Brand Perception Layer
                      </strong>
                      <p className="text-slate-600" data-id="element-246">
                        After each insight: attendee is asked how strongly peers
                        associate Minerva with solving this challenge (1-5
                        scale).
                      </p>
                    </li>
                    <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-247">
                      <strong className="text-slate-900 block text-base mb-1" data-id="element-248">
                        5. Segmentation & 6. Lead Capture
                      </strong>
                      <p className="text-slate-600" data-id="element-249">
                        Attendee identifies development focus, then enters name,
                        email, company, role to activate prize draw entry.
                      </p>
                    </li>
                    <li className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-250">
                      <strong className="text-slate-900 block text-base mb-1" data-id="element-251">
                        7. Conversion / Booth Traffic
                      </strong>
                      <p className="text-slate-600" data-id="element-252">
                        Attendee is encouraged to visit Minerva at booth #1762
                        (double raffle chances) with floor map included.
                      </p>
                    </li>
                  </ol>
                </section>

                <section data-id="element-253">
                  <h2 className="text-xl font-display font-bold text-slate-900 mb-3 border-b border-slate-200 pb-2" data-id="element-254">
                    Objectives & KPIs
                  </h2>
                  <div className="space-y-4 text-sm" data-id="element-255">
                    <div data-id="element-256">
                      <strong className="text-slate-900" data-id="element-257">
                        Primary Objectives:
                      </strong>
                      <ul className="list-disc pl-4 mt-1 text-slate-600 space-y-1" data-id="element-258">
                        <li data-id="element-259">Generate qualified leads</li>
                        <li data-id="element-260">
                          Drive booth engagement (Measurable uplift vs passive
                          traffic)
                        </li>
                        <li data-id="element-261">Increase brand awareness</li>
                      </ul>
                    </div>
                    <div data-id="element-262">
                      <strong className="text-slate-900" data-id="element-263">
                        Secondary Objectives:
                      </strong>
                      <ul className="list-disc pl-4 mt-1 text-slate-600 space-y-1" data-id="element-264">
                        <li data-id="element-265">
                          Gather market intelligence (bottlenecks, decision
                          drivers, priorities)
                        </li>
                        <li data-id="element-266">
                          Measure brand positioning (Minerva association
                          scoring)
                        </li>
                      </ul>
                    </div>
                    <div className="bg-minerva-blue/5 p-4 rounded-xl border border-minerva-blue/20 mt-4" data-id="element-267">
                      <strong className="text-minerva-blue block mb-2" data-id="element-268">
                        Success Definition
                      </strong>
                      <p className="text-slate-700 italic" data-id="element-269">
                        Success is NOT: badge scans.
                        <br data-id="element-270" />
                        Success is: engaged, qualified industry conversations
                        backed by measurable market insight and stronger Minerva
                        positioning.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}